import React, { useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    TextField,
    Theme,
    Checkbox,
    Typography,
} from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import { FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMaintenancePackage } from 'src/hooks/useMaintenancePackage';
import { useServiceClient } from 'src/hooks/useServiceClient';
import useAuth from 'src/hooks/useAuth';
import type { IMaintenancePackage } from 'src/interfaces/maintenancePackage';
import type { TCreateServiceClientVehicleServiceHistoryPayload } from 'src/contexts/serviceClient/types';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    MAINTENANCE_PACKAGE_AUTH,
    READ_PERMISSION,
} from 'src/constants/permissions';
import { Alert, Autocomplete } from '@material-ui/lab';
import PriceMask from 'src/utils/masks/NumberFormatPrice';
import { Button } from 'src/shared/ui';

const useStyles = makeStyles((theme: Theme) => ({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
    },
    detailsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
}));

type TNewServiceFormValues = {
    maintenancePackageId: string;
    serviceDate: string;
    deliveryDate: string;
    cost: string;
    description: string;
    details: string[];
    additional: { title: string; description: string }[];
};

const defaultValues: TNewServiceFormValues = {
    maintenancePackageId: '',
    serviceDate: '',
    deliveryDate: '',
    cost: '',
    description: '',
    details: [],
    additional: [{ title: '', description: '' }],
};

let hasRequestedMaintenancePackages = false;

const addDaysToDateInput = (value: string, days: number): string => {
    const [year, month, day] = value.split('-').map(Number);

    if (!year || !month || !day) return '';

    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + days);

    return date.toISOString().slice(0, 10);
};

type NewServiceFormProps = {
    serviceClientId?: string;
    vehicleId?: string;
    isEnabled?: boolean;
    onSuccess?: () => void;
};

export const NewServiceForm = ({
    serviceClientId,
    vehicleId,
    isEnabled = true,
    onSuccess,
}: NewServiceFormProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { user } = useAuth();
    const {
        maintenancePackages,
        searchMaintenancePackagesByFilters,
        loading: maintenancePackageLoading,
    } = useMaintenancePackage();
    const {
        createServiceClientVehicleServiceHistory,
        loading: serviceClientLoading,
        error: serviceClientError,
    } = useServiceClient();
    const canReadMaintenancePackages = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: MAINTENANCE_PACKAGE_AUTH,
        crud: READ_PERMISSION,
    });

    useEffect(() => {
        if (!canReadMaintenancePackages) return;
        if (hasRequestedMaintenancePackages) return;
        if (maintenancePackageLoading) return;
        if ((maintenancePackages || []).length > 0) return;

        hasRequestedMaintenancePackages = true;
        searchMaintenancePackagesByFilters({
            page: 1,
            limit: 200,
            name: [],
            'details.title': [],
            stores: [],
            isActive: true,
            updatedAt: '',
            isSearching: false,
            tabValue: '',
            isTabsEnabled: false,
            isQueryEnabled: false,
            query: '',
        });
    }, [
        canReadMaintenancePackages,
        maintenancePackageLoading,
        maintenancePackages,
        searchMaintenancePackagesByFilters,
    ]);

    const validationSchema = Yup.object({
        maintenancePackageId: Yup.string().required(t('Yup.Required')),
        serviceDate: Yup.string().required(t('Yup.Required')),
        deliveryDate: Yup.string().required(t('Yup.Required')),
        cost: Yup.number()
            .typeError(t('Yup.Number'))
            .required(t('Yup.Required'))
            .min(0, t('Yup.Number')),
        description: Yup.string().required(t('Yup.Required')),
    });

    return (
        <Formik
            initialValues={defaultValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) => {
                if (!serviceClientId || !vehicleId) return;

                const selectedPackage = (maintenancePackages || []).find(
                    (item: IMaintenancePackage) =>
                        item._id === values.maintenancePackageId
                );
                const selectedDetails = (selectedPackage?.details || []).filter(
                    (detail) => values.details.includes(detail.title)
                );
                const additionalDetails = values.additional
                    .map((detail) => ({
                        title: detail.title?.trim(),
                        description: detail.description?.trim(),
                    }))
                    .filter((detail) => detail.title || detail.description)
                    .map((detail) => ({
                        title: detail.title || '',
                        description: detail.description || detail.title || '',
                    }));

                const payload: TCreateServiceClientVehicleServiceHistoryPayload =
                {
                    maintenancePackage: values.maintenancePackageId,
                    price: Number(values.cost),
                    description: values.description,
                    details: selectedDetails,
                    additionalDetails,
                    serviceDate: values.serviceDate,
                    deliveryDate: values.deliveryDate,
                };

                const response = await createServiceClientVehicleServiceHistory(
                    serviceClientId,
                    vehicleId,
                    payload
                );

                if (response) {
                    resetForm();
                    onSuccess?.();
                }
            }}
        >
            {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                setFieldValue,
                resetForm,
                isSubmitting,
            }) => {
                const selectedMaintenancePackage = (
                    maintenancePackages || []
                ).find(
                    (item: IMaintenancePackage) =>
                        item._id === values.maintenancePackageId
                );
                const details = selectedMaintenancePackage?.details || [];

                return (
                    <Form>
                        <Card elevation={0}>
                            <CardHeader
                                title={String(
                                    t('serviceClient.NewServiceTitle')
                                )}
                            />
                            <Divider />
                            <CardContent className={classes.formContainer}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            error={
                                                touched.maintenancePackageId &&
                                                Boolean(
                                                    errors.maintenancePackageId
                                                )
                                            }
                                        >
                                            <InputLabel>
                                                {String(
                                                    t(
                                                        'serviceClient.SelectService'
                                                    )
                                                )}
                                            </InputLabel>
                                            <Select
                                                name="maintenancePackageId"
                                                value={
                                                    values.maintenancePackageId
                                                }
                                                disabled={
                                                    !isEnabled ||
                                                    !canReadMaintenancePackages
                                                }
                                                onChange={(event) => {
                                                    handleChange(event);

                                                    const selectedPackage = (
                                                        maintenancePackages ||
                                                        []
                                                    ).find(
                                                        (
                                                            item: IMaintenancePackage
                                                        ) =>
                                                            item._id ===
                                                            event.target.value
                                                    );

                                                    const packageDetails =
                                                        selectedPackage?.details?.map(
                                                            (detail) =>
                                                                detail.title
                                                        ) || [];

                                                    setFieldValue(
                                                        'details',
                                                        packageDetails
                                                    );
                                                }}
                                                onBlur={handleBlur}
                                                label={String(
                                                    t(
                                                        'serviceClient.SelectService'
                                                    )
                                                )}
                                            >
                                                {(
                                                    maintenancePackages || []
                                                ).map(
                                                    (
                                                        maintenancePackage: IMaintenancePackage
                                                    ) => (
                                                        <MenuItem
                                                            key={
                                                                maintenancePackage._id
                                                            }
                                                            value={
                                                                maintenancePackage._id
                                                            }
                                                        >
                                                            {
                                                                maintenancePackage.name
                                                            }
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                            <FormHelperText>
                                                {!canReadMaintenancePackages
                                                    ? String(
                                                        t(
                                                            'serviceClient.MaintenancePackageReadPermissionRequired'
                                                        )
                                                    )
                                                    : touched.maintenancePackageId &&
                                                    errors.maintenancePackageId}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            type="date"
                                            name="serviceDate"
                                            label="Service Date"
                                            value={values.serviceDate}
                                            disabled={!isEnabled}
                                            onChange={(event) => {
                                                handleChange(event);
                                                setFieldValue(
                                                    'deliveryDate',
                                                    addDaysToDateInput(
                                                        event.target.value,
                                                        1
                                                    )
                                                );
                                            }}
                                            onBlur={handleBlur}
                                            InputLabelProps={{ shrink: true }}
                                            error={
                                                touched.serviceDate &&
                                                Boolean(errors.serviceDate)
                                            }
                                            helperText={
                                                touched.serviceDate &&
                                                errors.serviceDate
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            type="date"
                                            name="deliveryDate"
                                            label={String(
                                                t('serviceClient.DeliveryDate')
                                            )}
                                            value={values.deliveryDate}
                                            disabled={!isEnabled}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            InputLabelProps={{ shrink: true }}
                                            error={
                                                touched.deliveryDate &&
                                                Boolean(errors.deliveryDate)
                                            }
                                            helperText={
                                                touched.deliveryDate &&
                                                errors.deliveryDate
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            name="cost"
                                            label={String(
                                                t('serviceClient.Cost')
                                            )}
                                            value={values.cost}
                                            disabled={!isEnabled}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.cost &&
                                                Boolean(errors.cost)
                                            }
                                            helperText={
                                                touched.cost && errors.cost
                                            }
                                            InputProps={{
                                                inputComponent: PriceMask,
                                                autoComplete: 'off',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            name="description"
                                            label={String(
                                                t(
                                                    'serviceClient.ServiceDescription'
                                                )
                                            )}
                                            value={values.description}
                                            disabled={!isEnabled}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.description &&
                                                Boolean(errors.description)
                                            }
                                            helperText={
                                                touched.description &&
                                                errors.description
                                            }
                                        />
                                    </Grid>
                                    {details.length > 0 && (
                                        <Grid item xs={12}>
                                            <Box
                                                className={
                                                    classes.detailsContainer
                                                }
                                            >
                                                {details.map((detail) => {
                                                    const detailTitle =
                                                        detail?.title || '';
                                                    const isChecked =
                                                        values.details.includes(
                                                            detailTitle
                                                        );

                                                    return (
                                                        <FormControlLabel
                                                            key={detailTitle}
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        isChecked
                                                                    }
                                                                    disabled={
                                                                        !isEnabled
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) => {
                                                                        const nextDetails =
                                                                            event
                                                                                .target
                                                                                .checked
                                                                                ? [
                                                                                    ...values.details,
                                                                                    detailTitle,
                                                                                ]
                                                                                : values.details.filter(
                                                                                    (
                                                                                        item
                                                                                    ) =>
                                                                                        item !==
                                                                                        detailTitle
                                                                                );

                                                                        setFieldValue(
                                                                            'details',
                                                                            nextDetails
                                                                        );
                                                                    }}
                                                                    name={
                                                                        detailTitle
                                                                    }
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={String(
                                                                t(detailTitle)
                                                            )}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Typography variant="h5">
                                            Additional Details
                                        </Typography>
                                        <FieldArray name="additional">
                                            {({ push, remove }) => (
                                                <Box mt={2}>
                                                    {values.additional.map(
                                                        (
                                                            additionalDetail,
                                                            index
                                                        ) => (

                                                            <Grid
                                                                container
                                                                spacing={2}
                                                                alignItems="center"
                                                                key={`additional-${index}`}
                                                            >
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    md={5}
                                                                >
                                                                    <Autocomplete
                                                                        freeSolo
                                                                        options={details.map(
                                                                            (
                                                                                d
                                                                            ) => d.title
                                                                        )}
                                                                        getOptionLabel={(
                                                                            option
                                                                        ) =>
                                                                            String(
                                                                                t(option)
                                                                            )
                                                                        }
                                                                        value={
                                                                            additionalDetail.title
                                                                        }
                                                                        onChange={(
                                                                            _,
                                                                            newValue
                                                                        ) => {
                                                                            if (
                                                                                newValue ===
                                                                                null
                                                                            ) {
                                                                                setFieldValue(
                                                                                    `additional.${index}.title`,
                                                                                    ''
                                                                                );
                                                                                setFieldValue(
                                                                                    `additional.${index}.description`,
                                                                                    ''
                                                                                );
                                                                                return;
                                                                            }
                                                                            const title =
                                                                                newValue;
                                                                            setFieldValue(
                                                                                `additional.${index}.title`,
                                                                                title
                                                                            );
                                                                            const match =
                                                                                details.find(
                                                                                    (
                                                                                        d
                                                                                    ) =>
                                                                                        d.title ===
                                                                                        title
                                                                                );
                                                                            if (match) {
                                                                                setFieldValue(
                                                                                    `additional.${index}.description`,
                                                                                    match.description ??
                                                                                    ''
                                                                                );
                                                                            }
                                                                        }}
                                                                        onInputChange={(
                                                                            _,
                                                                            newInputValue,
                                                                            reason
                                                                        ) => {
                                                                            if (
                                                                                reason ===
                                                                                'input'
                                                                            ) {
                                                                                setFieldValue(
                                                                                    `additional.${index}.title`,
                                                                                    newInputValue
                                                                                );
                                                                            }
                                                                        }}
                                                                        renderInput={(
                                                                            params
                                                                        ) => (
                                                                            <TextField
                                                                                {...params}
                                                                                variant="outlined"
                                                                                label={String(
                                                                                    t(
                                                                                        'maintenancePackage.DetailsTitle'
                                                                                    )
                                                                                )}
                                                                                disabled={
                                                                                    !isEnabled
                                                                                }
                                                                            />
                                                                        )}
                                                                        disabled={
                                                                            !isEnabled
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    md={6}
                                                                >
                                                                    <TextField
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        name={`additional.${index}.description`}
                                                                        label={String(
                                                                            t(
                                                                                'maintenancePackage.DetailsDescription'
                                                                            )
                                                                        )}
                                                                        value={
                                                                            additionalDetail.description
                                                                        }
                                                                        disabled={
                                                                            !isEnabled
                                                                        }
                                                                        onChange={
                                                                            handleChange
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    md={1}
                                                                >
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            remove(
                                                                                index
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            !isEnabled ||
                                                                            values
                                                                                .additional
                                                                                .length ===
                                                                            1
                                                                        }
                                                                    >
                                                                        <Delete />
                                                                    </IconButton>
                                                                </Grid>
                                                            </Grid>
                                                        )
                                                    )}
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        mt={1}
                                                    >
                                                        <Button
                                                            variant="text"
                                                            startIcon={<Add />}
                                                            onClick={() =>
                                                                push({
                                                                    title: '',
                                                                    description:
                                                                        '',
                                                                })
                                                            }
                                                            disabled={
                                                                !isEnabled
                                                            }
                                                        >
                                                            {String(
                                                                t(
                                                                    'maintenancePackage.AddDetail'
                                                                )
                                                            )}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            )}
                                        </FieldArray>
                                    </Grid>
                                </Grid>

                                {serviceClientError && (
                                    <Alert severity="error">
                                        {serviceClientError}
                                    </Alert>
                                )}

                                <Box className={classes.buttonsContainer}>
                                    <Button
                                        variant="contained"
                                        disabled={!isEnabled}
                                        onClick={() => resetForm()}
                                    >
                                        {String(t('Buttons.Cancel'))}
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={
                                            serviceClientLoading ||
                                            isSubmitting ||
                                            !isEnabled ||
                                            !canReadMaintenancePackages ||
                                            !serviceClientId ||
                                            !vehicleId
                                        }
                                    >
                                        {String(t('serviceClient.AddService'))}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Form>
                );
            }}
        </Formik>
    );
};
